<template>
  <v-dialog v-model="dialog" max-width="1200px" persistent scrollable>
    <v-card class="bulk-update-card">
      <v-card-title class="headline">
        <span>Bulk Ažuriranje KPD Šifri</span>
        <v-spacer></v-spacer>
        <v-btn icon @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text style="height: 70vh; max-height: 70vh;">
        <v-tabs v-model="activeTab" color="#00B3BE" class="mb-0">
          <v-tab>
            <v-icon left small>mdi-folder-multiple</v-icon>
            Po Kategoriji
          </v-tab>
          <v-tab>
            <v-icon left small>mdi-format-list-checks</v-icon>
            Po Artiklima
          </v-tab>
        </v-tabs>

        <v-tabs-items v-model="activeTab">
          <!-- Tab 1: By Category -->
          <v-tab-item>
            <v-card flat>
              <v-card-text>
                <v-row>
                  <v-col cols="12" md="6">
                    <v-autocomplete
                      v-model="applyToCategory"
                      :items="sortedCategories"
                      item-text="name"
                      item-value="id"
                      label="Odaberi kategoriju"
                      dense outlined hide-details
                      class="filter-select">
                      <template #prepend-inner>
                        <v-icon color="#3989C9" small>mdi-folder</v-icon>
                      </template>
                    </v-autocomplete>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="kpdCodeToApply"
                      label="Unesite KPD šifru"
                      dense outlined hide-details
                      class="filter-select">
                      <template #prepend-inner>
                        <v-icon color="#3989C9" small>mdi-barcode</v-icon>
                      </template>
                    </v-text-field>
                  </v-col>
                </v-row>

                <v-alert type="info" dense class="mt-4" v-if="applyToCategory">
                  Ažurirat će se SVI artikli u kategoriji "{{ getCategoryName(applyToCategory) }}"
                </v-alert>

                <v-row class="mt-4">
                  <v-col cols="12" class="text-center">
                    <v-btn
                      color="#3989C9"
                      dark
                      large
                      :disabled="!applyToCategory || !kpdCodeToApply"
                      @click="applyToCategoryAction">
                      <v-icon left>mdi-folder-multiple</v-icon>
                      Primijeni na cijelu kategoriju
                    </v-btn>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-tab-item>

          <!-- Tab 2: By Individual Articles -->
          <v-tab-item>
            <v-card flat>
              <v-card-text class="pa-0 articles-tab-container">
                <!-- Sticky Filters Header -->
                <div class="articles-filter-header">
                  <!-- Row 1: Search and filter inputs -->
                  <v-row dense class="mb-1">
                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model="filterSearchTerm"
                        label="Pretraži artikle"
                        dense outlined hide-details
                        class="filter-select"
                        @input="onFilterChange">
                        <template #append>
                          <v-icon color="#3989C9" small>mdi-magnify</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-autocomplete
                        v-model="filterCategories"
                        :items="sortedCategories"
                        item-text="name"
                        item-value="id"
                        label="Kategorije"
                        multiple dense outlined hide-details
                        small-chips
                        class="filter-select"
                        @change="onFilterChange">
                      </v-autocomplete>
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-select
                        v-model="filterHasKpdCode"
                        :items="kpdFilterOptions"
                        item-text="label"
                        item-value="value"
                        label="KPD Status"
                        dense outlined hide-details
                        class="filter-select"
                        @change="onFilterChange">
                      </v-select>
                    </v-col>
                  </v-row>

                  <!-- Row 2: Selected count, KPD input, and apply button -->
                  <v-row dense align="center" class="ma-0">
                    <v-col cols="12" md="4" class="d-flex align-center">
                      <v-chip color="#00B3BE" text-color="white" label small>
                        <v-icon left x-small>mdi-checkbox-marked-circle</v-icon>
                        <span class="text-caption">Odabrano: {{ selectedItemIds.size }}</span>
                      </v-chip>
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model="kpdCodeToApply"
                        label="KPD šifra"
                        dense outlined hide-details
                        class="filter-select">
                        <template #prepend-inner>
                          <v-icon color="#3989C9" small>mdi-barcode</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-btn
                        color="#00B3BE"
                        dark
                        small
                        block
                        :disabled="selectedItemIds.size === 0 || !kpdCodeToApply"
                        @click="applyToSelected">
                        <v-icon left x-small>mdi-check-all</v-icon>
                        Primijeni
                      </v-btn>
                    </v-col>
                  </v-row>
                </div>

                <!-- Scrollable Table Container -->
                <div class="table-scroll-container">
                  <!-- Data Table with Server-side Pagination -->
                  <v-data-table
                    :headers="headers"
                    :items="items"
                    :loading="isLoading"
                    hide-default-footer
                    disable-pagination
                    class="articles-table clickable-rows"
                    @click:row="toggleSelection">

                    <!-- Checkbox column -->
                    <template #item.selected="{ item }">
                      <div class="checkbox-wrapper" @click.stop="toggleSelection(item)">
                        <v-checkbox
                          :input-value="selectedItemIds.has(item.id)"
                          hide-details
                          color="#00B3BE"
                          readonly
                        />
                      </div>
                    </template>

                    <template #item.name="{ item }">
                      <div>
                        <div>{{ item.name }}</div>
                        <div v-if="item.code" class="caption grey--text">{{ item.code }}</div>
                      </div>
                    </template>

                    <template #item.categories="{ item }">
                      <v-chip
                        v-for="cat in item.categories"
                        :key="cat.id"
                        x-small
                        class="mr-1"
                        color="#3989C9"
                        text-color="white">
                        {{ cat.name }}
                      </v-chip>
                      <span v-if="!item.categories || !item.categories.length" class="grey--text caption">
                        -
                      </span>
                    </template>

                    <template #item.kpdcode="{ item }">
                      <v-chip
                        x-small
                        :color="item.kpdcode ? '#00B3BE' : 'grey'"
                        text-color="white">
                        {{ item.kpdcode || 'N/A' }}
                      </v-chip>
                    </template>
                  </v-data-table>

                  <!-- Custom Footer for Pagination -->
                  <div class="table-footer">
                    <div class="pagination-controls">
                      <v-btn
                        small
                        outlined
                        color="#00B3BE"
                        :disabled="!canGoPrevious"
                        @click="loadPreviousPage">
                        <v-icon left x-small>mdi-chevron-left</v-icon>
                        Prethodna
                      </v-btn>
                      <span class="mx-3 caption">{{ items.length }} artikala</span>
                      <v-btn
                        small
                        outlined
                        color="#00B3BE"
                        :disabled="!hasMore"
                        @click="loadNextPage">
                        Sljedeća
                        <v-icon right x-small>mdi-chevron-right</v-icon>
                      </v-btn>
                    </div>
                    <div class="items-per-page-select">
                      <span class="caption mr-2">Po stranici:</span>
                      <v-select
                        v-model="limit"
                        :items="[20, 40, 80]"
                        dense
                        outlined
                        hide-details
                        style="min-width: 90px;"
                        @change="onLimitChange"
                      ></v-select>
                    </div>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-tab-item>
        </v-tabs-items>

      </v-card-text>
    </v-card>

    <!-- Snackbars for notifications -->
    <v-snackbar v-model="errorSnackbar" color="error" :timeout="6000" top>
      {{ errorMessage }}
      <template #action="{ attrs }">
        <v-btn text v-bind="attrs" @click="errorSnackbar = false">
          Zatvori
        </v-btn>
      </template>
    </v-snackbar>

    <v-snackbar v-model="successSnackbar" color="success" :timeout="4000" top>
      {{ successMessage }}
    </v-snackbar>
  </v-dialog>
</template>

<script>
import { kpdSifraApi } from '@/api/kpdSifra'
import state from '@/state'

export default {
  name: 'BulkUpdateDialog',

  props: {
    categories: {
      type: Array,
      default: () => []
    }
  },

  data () {
    return {
      dialog: false,
      activeTab: 0,
      items: [],
      locationId: null,

      // Persistent selection across pages
      selectedItemIds: new Set(),

      // Filters (independent from main view)
      filterSearchTerm: '',
      filterCategories: [],
      filterHasKpdCode: null,
      kpdFilterOptions: [
        { label: 'Svi', value: null },
        { label: 'Ima KPD', value: true },
        { label: 'Nema KPD', value: false }
      ],

      // Table
      headers: [
        { text: '', value: 'selected', sortable: false, width: '50px', align: 'center' },
        { text: 'Naziv', value: 'name', sortable: false },
        { text: 'Kategorije', value: 'categories', sortable: false },
        { text: 'KPD Šifra', value: 'kpdcode', sortable: false }
      ],
      limit: 20,
      startAfter: null,
      hasMore: false,
      lastDocId: null,
      pageHistory: [], // Track previous page cursors
      isLoading: false,

      // Bulk actions
      kpdCodeToApply: '',
      applyToCategory: null,

      filterDebounce: null,

      // Snackbars
      errorSnackbar: false,
      errorMessage: '',
      successSnackbar: false,
      successMessage: ''
    }
  },

  computed: {
    sortedCategories () {
      return [...this.categories].sort((a, b) => a.name.localeCompare(b.name))
    },
    canGoPrevious () {
      return this.pageHistory.length > 0
    }
  },

  watch: {
    activeTab (newTab) {
      // Load items when switching to articles tab
      if (newTab === 1 && this.locationId) {
        this.resetPagination()
        this.loadItems()
      }
    }
  },

  methods: {
    showError (message) {
      this.errorMessage = message
      this.errorSnackbar = true
    },

    showSuccess (message) {
      this.successMessage = message
      this.successSnackbar = true
    },

    getCategoryName (categoryId) {
      const category = this.categories.find(c => c.id === categoryId)
      return category ? category.name : ''
    },

    open (locationId) {
      this.locationId = locationId
      this.dialog = true
      this.activeTab = 0 // Start with category tab
    },

    close () {
      this.dialog = false
      this.resetState()
    },

    resetState () {
      this.selectedItemIds.clear()
      this.filterSearchTerm = ''
      this.filterCategories = []
      this.filterHasKpdCode = null
      this.kpdCodeToApply = ''
      this.applyToCategory = null
      this.resetPagination()
      this.activeTab = 0
    },

    resetPagination () {
      this.startAfter = null
      this.hasMore = false
      this.lastDocId = null
      this.pageHistory = []
    },

    async loadItems () {
      if (!this.locationId) {
        return
      }

      this.isLoading = true
      try {
        const params = {
          companyId: state.getCurrentCompany().id,
          locationId: this.locationId,
          limit: this.limit,
          searchTerm: this.filterSearchTerm,
          categories: this.filterCategories,
          hasKpdCode: this.filterHasKpdCode
        }

        if (this.startAfter) {
          params.startAfter = this.startAfter
        }

        const response = await kpdSifraApi.fetchItems(params)

        this.items = response.items
        this.hasMore = response.has_more
        this.lastDocId = response.last_doc_id
      } catch (error) {
        console.error('Error loading items:', error)
        this.showError(error.parsedMessage || 'Greška pri učitavanju artikala')
      } finally {
        this.isLoading = false
      }
    },

    loadNextPage () {
      if (!this.hasMore || !this.lastDocId) {
        return
      }

      // Save current cursor for back navigation
      this.pageHistory.push(this.startAfter)
      this.startAfter = this.lastDocId
      this.loadItems()
    },

    loadPreviousPage () {
      if (this.pageHistory.length === 0) {
        return
      }

      // Pop the previous cursor
      this.startAfter = this.pageHistory.pop()
      this.loadItems()
    },

    onFilterChange () {
      clearTimeout(this.filterDebounce)
      this.filterDebounce = setTimeout(() => {
        this.resetPagination()
        this.loadItems()
      }, 500)
    },

    onLimitChange () {
      this.resetPagination()
      this.loadItems()
    },

    toggleSelection (item) {
      if (this.selectedItemIds.has(item.id)) {
        this.selectedItemIds.delete(item.id)
      } else {
        this.selectedItemIds.add(item.id)
      }
      // Force reactivity
      this.selectedItemIds = new Set(this.selectedItemIds)
    },

    async applyToSelected () {
      if (this.selectedItemIds.size === 0 || !this.kpdCodeToApply || !this.locationId) {
        return
      }

      try {
        await kpdSifraApi.bulkUpdateKpdCodes(
          state.getCurrentCompany().id,
          this.locationId,
          Array.from(this.selectedItemIds),
          this.kpdCodeToApply.trim()
        )

        this.showSuccess(`KPD šifra uspješno ažurirana za ${this.selectedItemIds.size} artikala`)

        this.$emit('updated')
        this.close()
      } catch (error) {
        console.error('Error in bulk update:', error)
        this.showError(error.parsedMessage || 'Greška pri bulk ažuriranju')
      }
    },

    async applyToCategoryAction () {
      if (!this.applyToCategory || !this.kpdCodeToApply || !this.locationId) {
        return
      }

      try {
        const response = await kpdSifraApi.bulkUpdateByCategory(
          state.getCurrentCompany().id,
          this.locationId,
          this.applyToCategory,
          this.kpdCodeToApply.trim()
        )

        this.showSuccess(`KPD šifra uspješno ažurirana za ${response.updated_count} artikala`)

        this.$emit('updated')
        this.close()
      } catch (error) {
        console.error('Error in bulk update by category:', error)
        this.showError(error.parsedMessage || 'Greška pri bulk ažuriranju po kategoriji')
      }
    }
  }
}
</script>

<style scoped>
.bulk-update-card {
  height: 80vh;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
}

.headline {
  background: linear-gradient(135deg, #00B3BE 0%, #3989C9 100%);
  color: white;
  flex-shrink: 0;
}

/* Compact tabs */
.v-tabs {
  margin-bottom: 0 !important;
}

.v-tabs-items {
  margin-top: 0 !important;
  flex: 1;
  overflow: hidden;
}

/* Filter Select Inputs */
.filter-select.v-text-field--outlined >>> fieldset {
  border-color: rgba(57, 137, 201, 0.2) !important;
  border-radius: 8px !important;
}

.filter-select.v-text-field--outlined:hover >>> fieldset {
  border-color: rgba(57, 137, 201, 0.4) !important;
}

.filter-select.v-text-field--outlined.v-input--is-focused >>> fieldset {
  border-color: #3989C9 !important;
  border-width: 2px !important;
}

.filter-select >>> .v-input__slot {
  background: rgba(255, 255, 255, 0.8) !important;
  backdrop-filter: blur(10px) !important;
  min-height: 36px !important;
  border-radius: 8px !important;
}

.filter-select.v-text-field--outlined >>> .v-input__control {
  border-radius: 8px !important;
}

.filter-select >>> .v-text-field__slot input {
  font-size: 13px !important;
  font-weight: 500 !important;
  color: rgba(70, 70, 80, 0.9) !important;
  letter-spacing: 0.3px !important;
}

.filter-select >>> label {
  font-size: 12px !important;
  font-weight: 500 !important;
  color: rgba(57, 137, 201, 0.8) !important;
}

.filter-select >>> .v-select__selection,
.filter-select >>> .v-text-field__slot input {
  font-size: 13px !important;
  font-weight: 500 !important;
  color: rgba(70, 70, 80, 0.9) !important;
  letter-spacing: 0.3px !important;
}

/* Filter Select Dropdown Menu */
.filter-select >>> .v-menu__content {
  background: rgba(255, 255, 255, 0.98) !important;
  backdrop-filter: blur(20px) !important;
  border-radius: 8px !important;
  border: 1px solid rgba(0, 179, 190, 0.15) !important;
  box-shadow: 0 8px 24px rgba(0, 179, 190, 0.12) !important;
  max-height: 300px !important;
}

.filter-select >>> .v-list-item {
  font-size: 13px !important;
  font-weight: 500 !important;
  transition: all 0.2s ease !important;
  min-height: 40px !important;
}

.filter-select >>> .v-list-item:hover {
  background: rgba(57, 137, 201, 0.08) !important;
}

.filter-select >>> .v-list-item--active {
  background: rgba(0, 179, 190, 0.12) !important;
  color: #3989C9 !important;
}

.filter-select >>> .v-simple-checkbox {
  color: #3989C9 !important;
}

/* Tab 2 Articles Container */
.articles-tab-container {
  display: flex;
  flex-direction: column;
  height: 62vh;
  max-height: 62vh;
}

/* Fixed Filter Header for Tab 2 */
.articles-filter-header {
  flex-shrink: 0;
  background: rgba(255, 255, 255, 0.98) !important;
  backdrop-filter: blur(20px) !important;
  padding: 8px 12px 6px 12px;
  border-radius: 0;
  box-shadow: 0 1px 4px rgba(0, 179, 190, 0.08);
  border-bottom: 1px solid rgba(0, 179, 190, 0.15);
  z-index: 2;
}

/* Scrollable Table Container */
.table-scroll-container {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 0;
}

.table-scroll-container::-webkit-scrollbar {
  width: 6px;
}

.table-scroll-container::-webkit-scrollbar-track {
  background: rgba(0, 179, 190, 0.05);
  border-radius: 3px;
}

.table-scroll-container::-webkit-scrollbar-thumb {
  background: rgba(0, 179, 190, 0.3);
  border-radius: 3px;
}

.table-scroll-container::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 179, 190, 0.5);
}

/* Table Styling */
.articles-table {
  background: transparent !important;
}

.articles-table >>> .v-data-table__wrapper {
  overflow-x: auto !important;
}

/* Compact table rows */
.articles-table >>> .v-data-table thead tr {
  height: 36px !important;
}

.articles-table >>> .v-data-table tbody tr {
  height: 40px !important;
  cursor: pointer !important;
  transition: background-color 0.2s ease !important;
}

.articles-table >>> .v-data-table tbody tr:hover {
  background-color: rgba(0, 179, 190, 0.08) !important;
}

/* Ensure cursor appears on all cells */
.articles-table >>> .v-data-table tbody tr td {
  cursor: pointer !important;
}

.articles-table >>> .v-data-table thead th {
  padding: 0 8px !important;
  font-size: 12px !important;
  font-weight: 600 !important;
}

.articles-table >>> .v-data-table tbody td {
  padding: 4px 8px !important;
  font-size: 13px !important;
  vertical-align: middle !important;
}

/* Center checkbox vertically - use deep selector */
.articles-table >>> .checkbox-wrapper {
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  height: 100% !important;
  min-height: 40px !important;
}

.articles-table >>> .v-data-table tbody td .v-input--checkbox {
  margin: 0 !important;
  padding: 0 !important;
}

.articles-table >>> .v-data-table tbody td .v-input--checkbox .v-input__slot {
  margin: 0 !important;
  padding: 0 !important;
}

.articles-table >>> .v-data-table tbody td .v-input--checkbox .v-input__control {
  height: auto !important;
}

/* Custom Footer */
.table-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: rgba(255, 255, 255, 0.98);
  border-top: 1px solid rgba(0, 179, 190, 0.15);
  position: sticky;
  bottom: 0;
  z-index: 2;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 8px;
}

.pagination-controls .v-btn {
  text-transform: none !important;
  font-size: 12px !important;
  letter-spacing: 0 !important;
}

.items-per-page-select {
  display: flex;
  align-items: center;
  gap: 8px;
}
</style>

<style>
/* Unscoped styles for table interactivity - MUST work */
.clickable-rows tbody tr {
  cursor: pointer !important;
}

.clickable-rows tbody tr:hover {
  background-color: rgba(0, 179, 190, 0.08) !important;
}

.clickable-rows tbody tr td {
  cursor: pointer !important;
}

.clickable-rows tbody tr td * {
  cursor: pointer !important;
}

.clickable-rows tbody tr td .v-chip,
.clickable-rows tbody tr td .v-chip *,
.clickable-rows tbody tr td span,
.clickable-rows tbody tr td div {
  cursor: pointer !important;
}

/* More specific selectors */
.v-dialog .articles-table.v-data-table tbody tr {
  cursor: pointer !important;
}

.v-dialog .articles-table.v-data-table tbody tr:hover {
  background-color: rgba(0, 179, 190, 0.08) !important;
}

.v-dialog .articles-table.v-data-table tbody tr td {
  cursor: pointer !important;
}

/* Checkbox vertical centering */
.v-dialog .articles-table .checkbox-wrapper {
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  min-height: 40px !important;
}

/* Force pointer on everything inside table */
table.clickable-rows tbody tr,
table.clickable-rows tbody tr *:not(.v-input--checkbox):not(.v-icon) {
  cursor: pointer !important;
}

/* Aggressive checkbox centering */
.clickable-rows .checkbox-wrapper {
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  height: 100% !important;
  min-height: 40px !important;
}

.clickable-rows tbody td:first-child {
  vertical-align: middle !important;
  padding: 0 !important;
}

.clickable-rows tbody td:first-child .checkbox-wrapper {
  padding: 8px !important;
}

.clickable-rows .v-input--checkbox {
  margin: 0 !important;
  padding: 0 !important;
}

.clickable-rows .v-input--checkbox .v-input__control {
  height: auto !important;
  min-height: auto !important;
}

.clickable-rows .v-input--checkbox .v-input__slot {
  margin: 0 !important;
  padding: 0 !important;
}
</style>
